/**
 * The EAGER half of the live draft canvas route (OL-322).
 *
 * Two jobs, both of which must happen before a byte of the page is sent:
 *
 * 1. **The guard and the seed.** `requireAuth` proves a session (this is
 *    `/orca/loops`, a MEMBER surface — `requireAdmin` would lock out the very
 *    people the Loop Studio is for), then `loopDraftBeforeLoad` reads
 *    `loops.drafts.get` on the server and dehydrates the answer into the draft's
 *    seed atom, so the first paint already carries the draft rather than a
 *    spinner that resolves to it.
 * 2. **The stylesheet.** React Flow's sheet is linked from HERE, the eager half,
 *    as a render-blocking `<link>` in the head the server sends. Imported from
 *    the lazy chunk instead it would reach the browser only after the entry JS
 *    had booted, and the graph would paint unstyled and then jump.
 *
 * The heavy half — the canvas, the graph library, the atoms — lives in
 * `$draftId.lazy.tsx` so none of it lands in the entry chunk. `loop-flow.css?url`
 * is a URL import, not a module import, so the chunk sweep
 * (`loop-flow.chunk.test.ts`) does not count it as pulling the viewer in.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import loopFlowCss from "~/components/loops/loop-flow.css?url";
import type { BeforeLoadArgs } from "~/lib/orca-ssr.ts";
import { loopDraftBeforeLoad } from "~/lib/orca-ssr.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The sheets the server-rendered draft depends on, as render-blocking links:
 * the orcaloops frame sheet (scoped under `.ol-app`, inert until the page
 * mounts the frame) and then the graph's own.
 *
 * @since 1.0.0
 */
export const loopDraftStylesheetLinks = [
  ...orcaloopsStylesheetLinks,
  { rel: "stylesheet", href: loopFlowCss },
];

/**
 * The document head for the draft canvas.
 *
 * @since 1.0.0
 */
export const loopDraftHead = () => ({ links: loopDraftStylesheetLinks });

/**
 * Guard first, then seed: an unauthenticated visitor is redirected before the
 * draft read is ever attempted.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const loopDraftPageBeforeLoad = async (args: BeforeLoadArgs) => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- the SSR seeder returns a Promise
  const seeded = await loopDraftBeforeLoad(args);
  return { auth, ...seeded };
};

export const Route = createFileRoute("/orca/loops/drafts/$draftId")({
  beforeLoad: loopDraftPageBeforeLoad,
  head: loopDraftHead,
});
